import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { IUsersAdministrationUserModel } from "../../../usersAdministration/usersList/models/usersAdministrationUserModel";
import {
  IMassSmsFormModel,
  massSmsFormDefaultValues,
  massSmsSchema,
} from "../models/massSmsFormModel";

export const useMassSmsDialog = (onClose: () => void) => {
  const queryClient = useQueryClient();
  const form = useForm<IMassSmsFormModel>({
    resolver: yupResolver(massSmsSchema),
    defaultValues: massSmsFormDefaultValues,
  });

  const { data: users, isLoading: isUsersLoading } = useApiQuery<
    IUsersAdministrationUserModel[]
  >({
    queryKey: [endpointsMetadata.user.getAll],
    endpoint: endpointsMetadata.user.getAll,
    globalIsLoading: false,
  });

  const userOptions = useMemo(
    () =>
      (users ?? [])
        .filter((u) => !u.isDeleted && !!u.phoneNumber)
        .map((u) => ({
          id: u.id,
          name: `${u.firstName} ${u.lastName} (${u.phoneNumber})`,
        })),
    [users]
  );

  const { mutate: sendMassSms, isLoading: isSending } = useApiMutation({
    onSuccess: () => {
      form.reset();
      onClose();
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [endpointsMetadata.communicationLogs.getSmsLogs] });
      }, 5000);
    },
  });

  const selectedUserIds = form.watch("userIds") ?? [];

  const handleSend = form.handleSubmit((data) => {
    const count = data.userIds?.length ?? 0;
    showCustomConfirmDialog(
      `Сигурни ли сте, че искате да изпратите <b>${count}</b> ${count === 1 ? "SMS" : "SMS-а"}?`,
      () => {
        sendMassSms({
          endpoint: endpointsMetadata.communicationLogs.sendMassSms,
          method: "post",
          payload: data,
          successMessage: `Успешно изпратихте ${count} ${count === 1 ? "SMS" : "SMS-а"}.`,
          invalidateKeys: [[endpointsMetadata.communicationLogs.getSmsLogs]],
        });
      },
      undefined,
      "warning"
    );
  });

  return {
    form,
    userOptions,
    isUsersLoading,
    isSending,
    selectedUserIds,
    handleSend,
  };
};
